import { sanitizeRichHtml, sanitizePlainText as sanitizePlainTextValue } from "../../../utils/richHtmlSanitizer";
import { CommentAnchorType } from "../../../services/types";

export interface AnnotatedBlock {
  index: number;
  html: string;
  text: string;
  anchors: CommentAnchorType[];
}

export function sanitizeGistBodyHtml(html: string): string {
  return sanitizeRichHtml(html);
}

export function sanitizePlainText(value: string | null | undefined): string {
  return sanitizePlainTextValue(value);
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

function getNodeText(html: string): string {
  const template = document.createElement("template");
  template.innerHTML = html;
  return template.content.textContent || "";
}

function getOuterHtml(node: ChildNode): string {
  if (node.nodeType === Node.ELEMENT_NODE) {
    return (node as HTMLElement).outerHTML;
  }

  return sanitizePlainTextValue(node.textContent || "");
}

const BLOCK_LEVEL_TAGS = new Set([
  "ADDRESS",
  "ARTICLE",
  "ASIDE",
  "BLOCKQUOTE",
  "DIV",
  "DL",
  "FIGCAPTION",
  "FIGURE",
  "FOOTER",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "HEADER",
  "HR",
  "LI",
  "MAIN",
  "NAV",
  "OL",
  "P",
  "PRE",
  "SECTION",
  "TABLE",
  "UL",
]);

function isMeaningfulTextNode(node: ChildNode): boolean {
  return node.nodeType === Node.TEXT_NODE && Boolean((node.textContent || "").trim());
}

function isElementNode(node: ChildNode): node is HTMLElement {
  return node.nodeType === Node.ELEMENT_NODE;
}

function isStandaloneBlockNode(node: ChildNode): boolean {
  if (!isElementNode(node)) return false;
  return BLOCK_LEVEL_TAGS.has(node.tagName.toUpperCase());
}

function isMeaningfulSourceNode(node: ChildNode): boolean {
  if (isMeaningfulTextNode(node)) return true;
  return isElementNode(node);
}

function wrapInlineRunAsBlock(inlineHtmlParts: string[]): string | null {
  const html = inlineHtmlParts.join("").trim();
  return html ? `<p>${html}</p>` : null;
}

/**
 * Rich text imported from editors/pastes is not always clean paragraph HTML.
 * Some Gists contain top-level inline siblings such as text + span + strong + span.
 * Visually this is one paragraph, but the old block builder treated every top-level
 * inline node as a separate annotation block, so same-line selections could fail as
 * "multi-block" selections.
 *
 * This normalizer keeps real block-level nodes separate, but groups adjacent
 * top-level inline/text nodes into one virtual paragraph block.
 */
function buildRenderableBlockHtmlList(template: HTMLTemplateElement): string[] {
  const sourceNodes = Array.from(template.content.childNodes).filter(isMeaningfulSourceNode);
  const blockHtmlList: string[] = [];
  let inlineRun: string[] = [];

  const flushInlineRun = () => {
    const wrapped = wrapInlineRunAsBlock(inlineRun);
    if (wrapped) blockHtmlList.push(wrapped);
    inlineRun = [];
  };

  sourceNodes.forEach((node) => {
    if (isStandaloneBlockNode(node)) {
      flushInlineRun();
      blockHtmlList.push(getOuterHtml(node));
      return;
    }

    inlineRun.push(getOuterHtml(node));
  });

  flushInlineRun();

  return blockHtmlList.length > 0 ? blockHtmlList : [""];
}

export function getAnchorDisplayText(anchor: CommentAnchorType): string {
  return (
    sanitizePlainText(anchor.selectedText) ||
    sanitizePlainText(anchor.contextText) ||
    "Selected passage"
  );
}

function textContains(haystack: string, needle: string | null | undefined): boolean {
  const normalizedHaystack = normalizeWhitespace(haystack);
  const normalizedNeedle = normalizeWhitespace(needle || "");
  if (!normalizedNeedle) return false;
  return normalizedHaystack.includes(normalizedNeedle);
}

function anchorHasTextEvidenceInBlock(anchor: CommentAnchorType, blockText: string): boolean {
  if (textContains(blockText, anchor.selectedText)) return true;
  if (textContains(blockText, anchor.contextText)) return true;

  const prefix = normalizeWhitespace(anchor.prefixText || "");
  const suffix = normalizeWhitespace(anchor.suffixText || "");
  const normalizedBlock = normalizeWhitespace(blockText);

  if (prefix.length >= 12 && normalizedBlock.includes(prefix)) return true;
  if (suffix.length >= 12 && normalizedBlock.includes(suffix)) return true;

  return false;
}

function findBestBlockIndexForAnchor(anchor: CommentAnchorType, blocks: AnnotatedBlock[]): number | null {
  const directIndex = typeof anchor.blockIndex === "number" ? anchor.blockIndex : null;

  if (directIndex !== null && blocks[directIndex]) {
    return directIndex;
  }

  const textMatch = blocks.find((block) => anchorHasTextEvidenceInBlock(anchor, block.text));
  return textMatch ? textMatch.index : null;
}

export function buildAnnotatedBlocks(
  html: string,
  anchors: CommentAnchorType[]
): { blocks: AnnotatedBlock[]; unplacedAnchors: CommentAnchorType[] } {
  const sanitizedHtml = sanitizeGistBodyHtml(html);
  const template = document.createElement("template");
  template.innerHTML = sanitizedHtml;

  const blockHtmlList = buildRenderableBlockHtmlList(template);

  const blocks: AnnotatedBlock[] = blockHtmlList.map((blockHtml, index): AnnotatedBlock => {
    const blockText = getNodeText(blockHtml);

    return {
      index,
      html: blockHtml,
      text: blockText,
      anchors: [] as CommentAnchorType[],
    };
  });

  const unplacedAnchors: CommentAnchorType[] = [];

  anchors.forEach((anchor) => {
    const blockIndex = findBestBlockIndexForAnchor(anchor, blocks);
    if (blockIndex === null || !blocks[blockIndex]) {
      unplacedAnchors.push(anchor);
      return;
    }

    blocks[blockIndex].anchors.push(anchor);
  });

  return { blocks, unplacedAnchors };
}

function collectTextNodes(root: Node): Text[] {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  return textNodes;
}

function getFallbackRangeFromSelectedText(blockText: string, selectedText: string): { start: number; end: number } | null {
  const cleanSelectedText = selectedText.trim();
  if (!cleanSelectedText) return null;

  const directIndex = blockText.indexOf(cleanSelectedText);
  if (directIndex >= 0) {
    return { start: directIndex, end: directIndex + cleanSelectedText.length };
  }

  const lowerBlockText = blockText.toLowerCase();
  const lowerSelectedText = cleanSelectedText.toLowerCase();
  const caseInsensitiveIndex = lowerBlockText.indexOf(lowerSelectedText);
  if (caseInsensitiveIndex >= 0) {
    return { start: caseInsensitiveIndex, end: caseInsensitiveIndex + cleanSelectedText.length };
  }

  return null;
}

function getAnchorRangeInBlock(anchor: CommentAnchorType, blockText: string): { start: number; end: number } | null {
  const startOffset = Number(anchor.startOffset);
  const endOffset = Number(anchor.endOffset);

  if (
    Number.isFinite(startOffset) &&
    Number.isFinite(endOffset) &&
    startOffset >= 0 &&
    endOffset > startOffset &&
    endOffset <= blockText.length
  ) {
    return { start: startOffset, end: endOffset };
  }

  return getFallbackRangeFromSelectedText(blockText, sanitizePlainText(anchor.selectedText));
}

function wrapPlainTextRangeInHtml(html: string, start: number, end: number): string {
  const template = document.createElement("template");
  template.innerHTML = html;

  const textNodes = collectTextNodes(template.content);
  let cursor = 0;

  textNodes.forEach((textNode) => {
    const value = textNode.nodeValue || "";
    const nodeStart = cursor;
    const nodeEnd = cursor + value.length;
    cursor = nodeEnd;

    if (nodeEnd <= start || nodeStart >= end) return;

    const localStart = Math.max(0, start - nodeStart);
    const localEnd = Math.min(value.length, end - nodeStart);
    if (localEnd <= localStart) return;

    const fragment = document.createDocumentFragment();
    const before = value.slice(0, localStart);
    const highlighted = value.slice(localStart, localEnd);
    const after = value.slice(localEnd);

    if (before) {
      fragment.appendChild(document.createTextNode(before));
    }

    if (highlighted) {
      const span = document.createElement("span");
      span.className = "anchored-comment-exact-highlight";
      span.textContent = highlighted;
      fragment.appendChild(span);
    }

    if (after) {
      fragment.appendChild(document.createTextNode(after));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  });

  return sanitizeGistBodyHtml(template.innerHTML);
}

export function renderBlockHtmlWithActiveAnchor(block: AnnotatedBlock, activeAnchorId: number | null): string {
  if (!activeAnchorId) return block.html;

  const activeAnchor = block.anchors.find((anchor) => anchor.id === activeAnchorId);
  if (!activeAnchor) return block.html;

  const range = getAnchorRangeInBlock(activeAnchor, block.text);
  if (!range) return block.html;

  return wrapPlainTextRangeInHtml(block.html, range.start, range.end);
}

export function getReplyCount(anchor: CommentAnchorType): number {
  return anchor.rootComment?.replies?.length || 0;
}
